import { EditOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Modal, Tooltip, message } from 'antd';
import axios from 'axios';
import { Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import PPResizableTable from '../../components/table/PPResizableTable';
import { getFormattedResponse, getObjectByKey } from '../../utils';
import { trimTextFields } from '../../utils/formRules';
import { getIndexColumn } from '../../utils/getIndexColumn';
import CreateUser from './CreateUser';
import UserForm from './UserForm';
import useUserStore from './userStore';
import './profilepage.less';

const DEFAULT_PAGE_SIZE = 10;

/** Хадгалахын өмнө эхэн/төгсгөлийн илүү зайг цэвэрлэх текст талбарууд */
const TRIMMABLE_TEXT_FIELDS = ['lastName', 'firstName', 'email', 'description'];

/** Багана тус бүрийн өргөн — заагаагүй бол DEFAULT_COLUMN_WIDTH */
const COLUMN_WIDTH = {
  emailVerified: 240,
  description: 260,
  email: 240,
};

const DEFAULT_COLUMN_WIDTH = 200;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Мэйл баталгаажуулалтын төлөв — өнгө нь profilepage.less дэх класснаас */
const VERIFIED_STATUS = {
  true: { label: 'Баталгаажсан', modifier: 'verified' },
  false: { label: 'Баталгаажаагүй', modifier: 'unverified' },
};

const VerifiedBadge = ({ verified }) => {
  const { label, modifier } = VERIFIED_STATUS[Boolean(verified)];

  return (
    <span className={`employee-status-badge employee-status-${modifier}`}>
      <span className="employee-status-dot" />
      {label}
    </span>
  );
};

const UserTab = () => {
  const isListChanged = useUserStore((state) => state.isListChanged);
  const [form] = Form.useForm();

  const [data, setData] = useState([]);
  const [metaColumns, setMetaColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [editRowId, setEditRowId] = useState();
  // Засах ажилтны мэдээллийг татаж буй мөрийн id — тухайн товч дээр spinner
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  useEffect(() => {
    fetchData();
    //eslint-disable-next-line
  }, [isListChanged]);

  const fetchData = async ({ page = 1, size = DEFAULT_PAGE_SIZE } = {}) => {
    setLoading(true);
    try {
      const { data: response } = await axios.post('/api/1.0/users/list', { page, size });
      const {
        list,
        columns,
        rest: { totalDocs = 0, limit = size } = {},
      } = getFormattedResponse(response);

      setMetaColumns(columns);
      setData(list);
      setPagination({ current: page, pageSize: limit, total: totalDocs });
    } catch (e) {
      console.error('Fetch Users: ', e);
    } finally {
      setLoading(false);
    }
  };

  const sendVerifyEmailLink = async (email) => {
    try {
      const { data } = await axios.post('/auth/resendVerifyEmailLink', { email });
      if (data.statusCode === 200) {
        message.success('Бүртгэл баталгаажуулах холбоос илгээгдлээ');
      }
    } catch (e) {
      console.error('Resend Verify Email: ', e);
    }
  };

  const confirmSendEmail = (email) => {
    Modal.confirm({
      title: 'Анхааруулга',
      icon: <ExclamationCircleOutlined />,
      content: 'Та баталгаажуулах имэйл илгээх үү?',
      okText: 'Тийм',
      cancelText: 'Үгүй',
      onOk: () => sendVerifyEmailLink(email),
    });
  };

  const handleEdit = async (id) => {
    setEditRowId(id);
    setLoadingUserId(id);
    try {
      const { data } = await axios.get(`/api/v1.0/users/${id}`);
      const fields = data?.result?.data;

      if (data?.statusCode === 200) {
        form.setFieldsValue({
          email: getObjectByKey(fields, 'email')?.value,
          phoneNumber: getObjectByKey(fields, 'phoneNumber')?.value,
          firstName: getObjectByKey(fields, 'firstName')?.value,
          lastName: getObjectByKey(fields, 'lastName')?.value,
          roleCode: getObjectByKey(fields, 'roleCode')?.value,
          roleName: getObjectByKey(fields, 'roleName')?.value,
          // Нууц үг хоосон нээгдэнэ — хэрэглэгч бичвэл л шинэчлэгдэнэ
          password: undefined,
          description: getObjectByKey(fields, 'description')?.value,
        });
      }

      setIsEdit(true);
    } catch (e) {
      console.error('Fetch User: ', e);
    } finally {
      setLoadingUserId(null);
    }
  };

  const submitEdit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Толгой/сүүлийн зайг тайрч хадгална — үүсгэх урсгалтай ижил
    const trimmed = { ...values, ...trimTextFields(values, TRIMMABLE_TEXT_FIELDS) };

    setSaving(true);
    try {
      // Хоосон нууц үгийг илгээхгүй — хуучин нууц үг хэвээр үлдэнэ
      const { password, ...rest } = trimmed;
      const payload = password ? trimmed : rest;

      const { data } = await axios.post(`/api/v1.0/users/${editRowId}`, payload);

      if (data?.statusCode !== 200) return;

      message.success('Амжилттай шинэчлэгдлээ');
      setIsEdit(false);
      fetchData({ page: pagination.current, size: pagination.pageSize });
    } catch (e) {
      console.error('Update User: ', e);
    } finally {
      setSaving(false);
    }
  };

  /** Мэйл баталгаажуулалтын нүд: төлөв + дахин илгээх товч */
  const renderVerified = (_, record) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <VerifiedBadge verified={record.emailVerified} />
      {!record.emailVerified && (
        <Tooltip title="Баталгаажуулах холбоос илгээх">
          <div className="btn-send-container" onClick={() => confirmSendEmail(record.email)}>
            <Send style={{ width: 16, height: 16 }} />
          </div>
        </Tooltip>
      )}
    </div>
  );

  const columns = useMemo(
    () => [
      getIndexColumn(pagination.current, pagination.pageSize),
      ...metaColumns.map((col) => {
        const key = col.key ?? col.dataIndex;

        return {
          title: col.title,
          dataIndex: col.dataIndex,
          key,
          width: COLUMN_WIDTH[key] ?? DEFAULT_COLUMN_WIDTH,
          render: key === 'emailVerified' ? renderVerified : (value) => val(value),
        };
      }),
      {
        key: 'action',
        title: '',
        width: 72,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="default"
            icon={<EditOutlined style={{ fontSize: 14 }} />}
            loading={loadingUserId === record?._id}
            disabled={Boolean(loadingUserId) && loadingUserId !== record?._id}
            onClick={() => handleEdit(record?._id)}
          />
        ),
      },
    ],
    //eslint-disable-next-line
    [metaColumns, pagination, loadingUserId]
  );

  const handleTableChange = (nextPagination) =>
    fetchData({ page: nextPagination.current, size: nextPagination.pageSize });

  return (
    <div className="pp_table_wrapper">
      <div className="branch_tab_header">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAdd(true)}>
          Ажилтан нэмэх
        </Button>
      </div>

      <PPResizableTable
        dataSource={data}
        columns={columns}
        size="middle"
        rowKey="_id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
        scroll={{ x: 'max-content' }}
      />

      {isEdit && (
        <Modal
          title="Ажилтан засах"
          open={isEdit}
          onCancel={() => setIsEdit(false)}
          onOk={submitEdit}
          okText="Хадгалах"
          cancelText="Буцах"
          confirmLoading={saving}
          maskClosable={false}
          destroyOnClose
        >
          <UserForm form={form} isEdit />
        </Modal>
      )}

      {isAdd && <CreateUser visible={isAdd} onCancel={() => setIsAdd(false)} />}
    </div>
  );
};

export default UserTab;
